import generateUUID from '../../../_utils/math/generateUUID'

const _DEFAULT_OPTIONS = {
  depthFunc: 'LEQUAL',
  depthTest: true,
  blendDst: 'ONE_MINUS_SRC_COLOR',
  blendDstAlpha: 'ONE',
  blendEquation: 'FUNC_ADD',
  blendEquationAlpha: 'ONE',
  blending: 'normal',  // normal, additive, subtractive, multiply, custosm
  blendSrc: 'SRC_ALPHA',
  blendSrcAlpha: 'ONE',
  cullFace: false
}

export default class Material {
  constructor(options) {
    this._uniLocations = {};
    this._uuid = generateUUID();
    this._isDisposed = false;
    this._blending = '';
    this._glBlendEquation = '';
    this._glBlendEquationAlpha = '';
    this._glBlendSrc = '';
    this._glBlendDst = '';
    this._glBlendSrcAlpha = '';
    this._glBlendDstAlpha = '';
    this._enableFuncLabel = '';

    this._glProgram = null;

    this.vertexShader = options.vertexShader;
    this.fragmentShader = options.fragmentShader;
    this.uniforms = options.uniforms;

    options = Object.assign({}, _DEFAULT_OPTIONS, options);

    for (let key in options) {
      this[key] = options[key];
    }
  }

  get uuid() {
    return this._uuid;
  }

  get glProgram() {
    return this._glProgram;
  }

  get isDesposed() {
    return this._isDisposed;
  }

  set blending(value) {
    this._blending = value;
    this.updateBlendingValues();
  }

  get blending() {
    return this._blending;
  }

  dispose() {
    if(this._isDisposed) return;

    this._deleteGLProgram();
    this._deleteGLShaders();
    this._gl = null;

    this._isDisposed = true;
  }

  enableCullingSetting() {
    if(this.cullFace) {
      this._gl.enable(this._gl.CULL_FACE);
    } else {
      this._gl.disable(this._gl.CULL_FACE);
    }
  }

  enableDepthSetting() {
    if(this.depthTest) {
      this._gl.enable(this._gl.DEPTH_TEST);
      this._gl.depthFunc(this._gl[this.depthFunc]);
    } else {
      this._gl.disable(this._gl.DEPTH_TEST);
    }
  }

  enableBlendSetting() {
    this._gl[this._enableFuncLabel](this._gl.BLEND);
    this._gl.blendEquationSeparate(this._glBlendEquation, this._glBlendEquationAlpha);
    this._gl.blendFuncSeparate(this._glBlendSrc, this._glBlendDst, this._glBlendSrcAlpha, this._glBlendDstAlpha);
  }

  enableUniforms() {
    // uniforms
    this._uniLocations = {}
    let uniform, uniformLocation;
    for(let name in this.uniforms) {
      uniform = this.uniforms[name];

      uniformLocation = this._gl.getUniformLocation(this._glProgram, name);
      this._uniLocations[name] = uniformLocation;

      if(uniform.type.indexOf('Matrix') >= 0) {
        // matrix
        this._gl[uniform.type](uniformLocation, false, uniform.value);

      } else if(uniform.type.indexOf('texture') >= 0) {
        // texture
        uniform.value && uniform.value.bind(uniformLocation);

      } else {
        // uniform3f etc...
        let value = uniform.value;
        if(!isNaN(uniform.value)) value = [uniform.value];

        let arr = [uniformLocation];
        if(uniform.type.indexOf('v') >= 0) {
          // uniform1fv etc
          arr.push(Array.prototype.slice.call(value));
        } else {
          arr = arr.concat(Array.prototype.slice.call(value));
        }
        this._gl[uniform.type].apply(this._gl, arr);
      }
    }
  }

  setGLContext(gl) {
    this._gl = gl;
    this._createGLProgram();
    let uniform;
    for(let name in this.uniforms) {
      uniform = this.uniforms[name];
      if(uniform.type.indexOf('texture') >= 0) {
        // texture
        if(uniform.value && uniform.value.setGLContext) {
          uniform.value.setGLContext(this._gl);
        }
      }
    }
    this.updateBlendingValues();
  }

  getGLShaders() {
    return this._glShaders;
  }

  updateGLShaders() {
    this._deleteGLShaders();
    this._createGLShaders();
  }

  setTextureUnitIndicies(textureUnitIndexCnt) {
    let uniform;
    for(let name in this.uniforms) {
      uniform = this.uniforms[name];
      if(uniform.type.indexOf('texture') >= 0) {
        if(uniform.value) {
          uniform.value.textureUnitIndex = textureUnitIndexCnt++;
        }
      }
    }
    return textureUnitIndexCnt;
  }

  _updateGLProgram() {
    this._deleteGLProgram();
    this._createGLProgram();
  }

  _createGLProgram() {
    this._createGLShaders();

    this._glProgram = this._gl.createProgram();
    this._gl.attachShader(this._glProgram, this._glShaders.vertexShader);
    this._gl.attachShader(this._glProgram, this._glShaders.fragmentShader);
    this._gl.linkProgram(this._glProgram);
    if(this._gl.getProgramParameter(this._glProgram, this._gl.LINK_STATUS)){
      return this._glProgram;
    } else {
      // 失敗していたらエラーログをアラートする
      throw new Error(this._gl.getProgramInfoLog(this._glProgram));
    }
  }

  _detachGLShaders() {
    let shaders = this._gl.getAttachedShaders(this._glProgram);
    for (var i = 0, l = shaders.length; i < l; i++) {
      this._gl.detachShader(this._glProgram, shaders[i]);
    }
  }

  _deleteGLProgram() {
    if(!this._glProgram) return;
    this._detachGLShaders();
    this._gl.deleteProgram(this._glProgram);
    this._glProgram = null;
  }

  _createGLShaders() {
    this._glShaders = {
      vertexShader: this._createGLShader(this.vertexShader, this._gl.VERTEX_SHADER),
      fragmentShader: this._createGLShader(this.fragmentShader, this._gl.FRAGMENT_SHADER)
    };
    return this._glShaders;
  }

  _deleteGLShaders() {
    if(this._glShaders) {
      this._gl.deleteShader(this._glShaders.vertexShader);
      this._gl.deleteShader(this._glShaders.fragmentShader);
      this._glShaders = null;
    }
  }

  // shaderStr: srouce code string of shader
  // shaderType: this._gl.VERTEX_SHADER or this._gl.FRAGMENT_SHADER
  _createGLShader(shaderStr, shaderType) {
    // シェーダを格納する変数
    const glShader = this._gl.createShader(shaderType);
    this._gl.shaderSource(glShader, shaderStr);
    this._gl.compileShader(glShader);
    if(this._gl.getShaderParameter(glShader, this._gl.COMPILE_STATUS)) {
      return glShader;
    } else {
      // 失敗していたらエラー
      throw new Error(this._gl.getShaderInfoLog(glShader));
    }
  }

  updateBlendingValues() {
    if(!this._gl) return;

    if(this._blending) {
      this._enableFuncLabel = 'enable';
    } else {
      this._enableFuncLabel = 'disable';
    }

    if(this._blending == 'custom') {
      this._glBlendEquation = this._gl[this.blendEquation];
      this._glBlendEquationAlpha = this._gl[this.blendEquationAlpha];
      this._glBlendSrc = this._gl[this.blendSrc];
      this._glBlendDst = this._gl[this.blendDst];
      this._glBlendSrcAlpha = this._gl[this.blendSrcAlpha];
      this._glBlendDstAlpha = this._gl[this.blendDstAlpha];

    } else {
      this._glBlendEquation = this._gl.FUNC_ADD;
      this._glBlendEquationAlpha = this._gl.FUNC_ADD;
      this._glBlendSrcAlpha = this._gl.ONE;
      this._glBlendDstAlpha = this._gl.ONE;

      if(this._blending == 'normal') {
        this._glBlendSrc = this._gl.SRC_ALPHA;
        this._glBlendDst = this._gl.ONE_MINUS_SRC_ALPHA;

      } else if(this._blending == 'additive') {
        this._glBlendSrc = this._gl.SRC_ALPHA;
        this._glBlendDst = this._gl.ONE;

      } else if(this._blending == 'subtractive') {
        this._glBlendEquation = this._gl.FUNC_REVERSE_SUBTRACT;
        this._glBlendSrc = this._gl.SRC_ALPHA;
        this._glBlendDst = this._gl.DST_ALPHA;

      } else if(this._blending == 'multiply') {
        this._glBlendSrc = this._gl.DST_COLOR;
        this._glBlendDst = this._gl.ZERO;
      }
    }
  }
}
